import { t } from "../utils/i18n.ts";
import type { EventBus } from "../core/EventBus.ts";
import type { Card } from "../entities/types.ts";
import { PHASE_BUILD, PHASE_COMBAT, type RuntimeState } from "../game/types.ts";
import { syncGold, enterPause, exitPause } from "../game/stateIntents.ts";
import { COLORS } from "../core/constants.ts";
import { RARITY } from "../core/palette.ts";
import { mixHex } from "../rendering/EmissiveMaterial.ts";
import type { GameLoop } from "../engine/GameLoop.ts";
import { CommentaryTV } from "./CommentaryTV.ts";
import type { TvPopOutBeat } from "./OperatorMascot.ts";
import {
  createAudioMixer,
  type AudioMixer,
  type AudioMixerInit,
} from "./AudioMixer.ts";
import { CommsDirector } from "./CommsDirector.ts";
import { pickCommanderQuip } from "./commanderQuips.ts";
import { commsLine } from "./commsI18n.ts";
import { DEFAULT_INITIALS } from "../core/initials.ts";
import { uiChrome } from "./UIChromeCanvas.ts";
import {
  UI_FONT_STACK,
  UI_TYPE,
  UI_EASE,
  UI_DURATION,
  UI_SPACE,
  withUiScale,
  roleFontSize,
} from "./uiTokens.ts";
import { integrityState, integrityColor } from "./ProBar.ts";
import { PANEL } from "../rendering/designTokens.ts";
import { FINAL_WAVE } from "../core/config.ts";
import { InfoTooltip } from "./InfoTooltip.ts";
import { isReducedMotion } from "../engine/MotionSettings.ts";
import { actForWave, type ActIdentity } from "./actLegibility.ts";
import { clientLenToStage } from "../utils/stage.ts";
import { gearMenuStyles } from "./GearMenu.ts";
import { SettingsOverlay } from "./SettingsOverlay.ts";
import { announceAssertive } from "./a11y.ts";

/**
 * The PAUSE modal, as the HUD needs to see it (spec
 * `2026-pause-settings-overlays` AC-7/AC-16). `main.ts` owns the instance; the
 * HUD only needs to know whether it is up (that IS the entry path the swap has
 * to remember) and to be able to take it down / put it back. Structural, so
 * `PauseOverlay` satisfies it without importing anything new.
 */
export interface PauseSurface {
  show(): void;
  hide(): void;
  readonly visible: boolean;
}

/**
 * BL-63 — the multiplier-transparency readout, pulled live from the ScoreSystem
 * (a callback, not the instance, so the HUD never imports a systems module). All
 * fields are read-only snapshots; the HUD never writes back.
 */
export interface ComboReadout {
  /** The active combo tier (×N; 1 at rest). */
  multiplier: number;
  /** The current kill-chain count. */
  combo: number;
  /** Fraction 0..1 of the combo window still open (`comboWindowRemaining()`). */
  windowRemaining: number;
  /** Skill window multiplier (≠1 → a `+WINDOW` boost is active). */
  timeoutMult: number;
  /** Skill score multiplier (≠1 → a `+MULT` boost is active). */
  scoreMult: number;
}

/** A live combo-state provider (wired from `ScoreSystem` in `main.ts`). */
export type MultiplierSource = () => ComboReadout;

/**
 * BL-63 — the score-race wiring (passed from `main.ts`). `fetchTopScore`
 * resolves the live global rank-1 (or null when disabled / empty / failed —
 * NEVER throwing); `getLocalBest` is the always-available personal-best fallback
 * so the race bar always has a target. The HUD names neither the leaderboard
 * service nor persistence types (architecture-boundary safe).
 */
export interface ScoreRaceConfig {
  fetchTopScore: () => Promise<{ score: number; initials: string } | null>;
  getLocalBest: () => number;
}

/**
 * BL-63 — the wave heat colour for a fill position `t` (0..1): green → brass →
 * red, mixed FROM central palette anchors (no new hex). The wave bar paints each
 * segment with `waveHeatColor(i / (n-1))` so the fill reads as progress AND heat.
 */
export function waveHeatColor(t: number): string {
  const x = t < 0 ? 0 : t > 1 ? 1 : t;
  return x < 0.5
    ? mixHex(COLORS.SENTINEL_GREEN, COLORS.AMBER_WARN, x / 0.5)
    : mixHex(COLORS.AMBER_WARN, COLORS.ORACLE_RED, (x - 0.5) / 0.5);
}

/** Discrete segment count for the wave heat bar (the §② "~10–12 segments"). */
const WAVE_SEGMENTS = 12;

/** Discrete segment count for the integrity meter (the signed mockup's 10 `.seg`). */
const INTEGRITY_SEGMENTS = 10;

/** HUD-owned multiplier-timer redraw throttle (~30 fps; cheap, combo-gated). */
const MULT_FRAME_MS = 1000 / 30;

/**
 * HUD overlay — credits, the four lit reads (integrity · wave · score-race ·
 * multiplier) and the ⚙ controls. Subscribes to EventBus for reactive updates.
 * Layer 4 (HTML/CSS) per architecture spec.
 */
export class HUD {
  /** The TOP HUD bar (`#hud-bar`, in `#hud-row`) — Score + Multiplier + the
   *  ⚙/speed controls. */
  private container: HTMLDivElement;
  /** BL-140 — the LEFT HUD (`#hud-left`, docked in the left column) — Credits ·
   *  Integrity · Wave. Built from the SAME `.hud-cell` material as the top bar
   *  (vertical dividers → horizontal) so top + left read as one symmetric,
   *  premium frame. Replaces the removed SYS.MONITOR. */
  private leftContainer: HTMLDivElement;
  private creditsEl: HTMLSpanElement;
  /** BL-56/S98 — the integrity headline: a clean `INTEGRITY ♥ N` label over a
   *  10-segment CSS lit-cell bar (green → warn amber → danger red by level),
   *  ported 1:1 from the signed `top_hud_v2.html` `.seg` mockup. */
  private integValEl!: HTMLSpanElement;
  private integSegEl!: HTMLElement;
  private maxLives = NaN;
  /** BL-58/63/S98 — the WAVE readout host; the crisp number lives in `waveNumEl`,
   *  and the heat-filling 12-segment CSS bar (green→brass→red toward the
   *  finale) in `waveSegEl`. D-334 — the procedural flame canvas (`WaveFire`)
   *  that used to sit behind this cell is retired (owner decision, superseding
   *  the D-323 flame-octave work); the number still warms via `--heat`. */
  private waveEl!: HTMLElement;
  private waveNumEl!: HTMLSpanElement;
  private waveMaxEl!: HTMLSpanElement;
  private waveSegEl!: HTMLElement;
  /** BL-121/D-335 — the persistent, glanceable act marker ("ACT II"),
   *  colour-stepped per act so any-wave a screenshot answers "which act?".
   *  D-335 dropped the subtitle ("· HER SIGNAL") from this cell — it now
   *  shares the WAVE cell's ONE line with the wave counter. */
  private actBadgeEl!: HTMLSpanElement;
  /** D-335 correction (docs/decisions.md, amended) — the wave-read's own row
   *  (`.hud-wave-cell > .hud-row`), kept as an instance ref so the act
   *  badge's fit can be re-measured after its text changes. */
  private waveRowEl!: HTMLSpanElement;
  /** D-335 correction — re-measures the wave row on resize (a rail-width or
   *  chrome-scale change from an orientation flip can change whether the
   *  WORD form still fits), independent of the top-bar's own responsive
   *  observer (`resizeObs`, below). */
  private waveRowResizeObs: ResizeObserver | null = null;
  private prevAct = NaN;
  /** BL-63/S98 — the SCORE race: a crisp number + the `.race` track (fill + the
   *  `#1` target marker) + the WORLD-LEAD overflow flag + the `YOU · vs #1` meta
   *  line — the signed mockup's `.race` cell. */
  private scoreEl!: HTMLElement;
  private scoreNumEl!: HTMLSpanElement;
  private raceFillEl!: HTMLElement;
  private raceRecEl!: HTMLElement;
  private scoreLeadFlag!: HTMLSpanElement;
  private scoreVsEl!: HTMLSpanElement;
  /** BL-63 — the always-visible multiplier transparency cell. */
  private multCell: HTMLElement;
  /** CR-5 — the shared card-style tooltip explaining the MULT (kill-chain). */
  private readonly multTooltip = new InfoTooltip();
  private multValEl: HTMLSpanElement;
  private multTimerFill: HTMLElement;
  private multBrkEl: HTMLSpanElement;
  private endlessEl: HTMLSpanElement;
  private synergyEl: HTMLSpanElement;
  private audioMixer: AudioMixer | null = null;
  /** BL-63/BL-130 — the ⚙ button. Built by hand (not by {@link GearMenu}) since
   *  spec `2026-pause-settings-overlays` AC-5 retired the in-game dropdown: the
   *  button keeps `GearMenu`'s id/classes/material via `gearMenuStyles`, and
   *  what it opens is {@link settings}. Boot / PreRun keep the real dropdown. */
  private gearBtn: HTMLButtonElement;
  /** 2026-08-11 — the touch-reachable PAUSE entry. See its construction. */
  private pauseBtn: HTMLButtonElement;
  /** The in-run SETTINGS modal — the ONE surface the ⚙ opens (spec AC-4). */
  private settings: SettingsOverlay;
  private ffBtn: HTMLButtonElement;
  private speedEl: HTMLSpanElement;
  private resizeObs: ResizeObserver | null = null;
  private unsubscribers: (() => void)[] = [];
  private unregChrome: (() => void) | null = null;
  /** BL-140 — the left HUD's own engine-material chrome registration, so it
   *  lights with the SAME lit edge as the top bar (material parity). */
  private unregChromeLeft: (() => void) | null = null;
  /** Last-rendered values, so a counter only flashes when it actually changes. */
  private prevGold = NaN;
  private prevWave = NaN;
  private prevScore = NaN;
  private flashTimers = new Map<HTMLElement, ReturnType<typeof setTimeout>>();
  private onOpenLibrary: (() => void) | null = null;
  private gameLoop: GameLoop | null = null;
  private ffActive = false;
  private paused = false;
  /** O-11a — true only while the CURRENT pause was caused by opening the gear
   *  (vs. an external pause, e.g. ESC), so closing the gear only resumes the
   *  run it itself paused — see {@link onGearOpen}/{@link onGearClose}. */
  private pausedByGear = false;
  /** AC-16 — the pause modal (`main.ts` owns it), injected via
   *  {@link setPauseSurface}. null in headless/test hosts that have no pause
   *  surface: the gear then behaves exactly as it did before the swap. */
  private pauseSurface: PauseSurface | null = null;
  /** AC-16 — the settings trip's ENTRY PATH: true when settings was reached by
   *  swapping OUT of the pause modal, so `ZURÜCK`/Escape swaps BACK to it
   *  instead of resuming. See {@link openSettings}. */
  private settingsFromPause = false;
  /** Retained so the HUD's own control can raise the PAUSE surface the same way
   *  Escape does. See the pause button's construction for why that button no
   *  longer opens settings directly. */
  private bus: EventBus;
  private runtimeState: RuntimeState;
  /** BL-61 — the bottom-left commentary TV (the live-call broadcast surface that
   *  replaced the framed lit-CRT portrait). Driven by the comms arbiter as one
   *  `MascotSurface` implementation. */
  private commentaryTV: CommentaryTV | null = null;
  private comms: CommsDirector | null = null;
  private operatorInitials = DEFAULT_INITIALS;
  // ── BL-63 score-race state ───────────────────────────────────────────────
  private scoreRace: ScoreRaceConfig | null = null;
  private scoreTarget = 0;
  private scoreTargetIsGlobal = false;
  private scoreHolder = "";
  private liveScore = 0;
  private inLead = false;
  // ── BL-63 multiplier state ───────────────────────────────────────────────
  private multSource: MultiplierSource | null = null;
  private multRaf = 0;
  private multLastDraw = 0;
  private prevBrk = "";

  constructor(
    parent: HTMLElement,
    gs: RuntimeState,
    eventBus: EventBus,
    /** Retained for call-site compatibility; the WAVE-FIRE heat ramp is driven
     *  by the canonical `FINAL_WAVE` (BL-58), not the wave-template count. */
    _totalWaves = 1,
    /** BL-62 — the LEFT-column dock (`#comms-dock`) the commentary TV mounts
     *  into as a permanent in-flow fixture. (Was `#ui-layer` — a floating
     *  overlay; now docked into the column per the signed composition.) */
    commsDock?: HTMLElement,
    /** BL-140 — the LEFT-column host (`#status-row`) the left HUD (Credits ·
     *  Integrity · Wave) docks into. Absent (headless / tests) → the left HUD
     *  falls back into the HUD's own `parent` so its reads stay queryable. */
    statusHost?: HTMLElement,
    audioInit: AudioMixerInit = {
      sfxMuted: false,
      musicMuted: false,
      sfxVolume: 0.9,
      musicVolume: 0.6,
    },
    /** Task-013 (AC-17) — forwarded straight to the `CommsDirector`, which
     *  fires it on the SAME three pop-out beats it hands the TV surface (boss
     *  spawn / new-signature debut / breach). The HUD stays platform-agnostic
     *  (no Capacitor import): the caller (main.ts, under the `__NATIVE_SHELL__`
     *  build guard) wires this to the native haptics adapter. */
    onPopOutBeat?: (beat: TvPopOutBeat) => void,
    /** Spec `2026-pause-settings-overlays` (AC-4) — the full-stage layer the
     *  centered SETTINGS modal mounts into (`#ui-layer`, the same host
     *  `PauseOverlay` uses in main.ts). The modal is `position:absolute;
     *  inset:0`, so mounting it in the HUD's own `parent` (`#hud-row`, a thin
     *  strip) would confine it to that strip. Absent (headless / tests) → it
     *  falls back into `parent` so its reads stay queryable, exactly like
     *  {@link statusHost}. */
    overlayHost?: HTMLElement,
  ) {
    this.runtimeState = gs;
    this.bus = eventBus;
    this.container = document.createElement("div");
    this.container.id = "hud-bar";
    // BL-140 — the left HUD shares the top bar's lit-CRT material; a11y group so
    // AT announces it as the "mainframe status" cluster.
    this.leftContainer = document.createElement("div");
    this.leftContainer.id = "hud-left";
    this.leftContainer.setAttribute("role", "group");
    this.leftContainer.setAttribute("aria-label", t("hud.statusLabel"));
    this.injectStyles();

    // S146/BL-140 — every cell keeps the signed two-row `.hud-cell` rhythm (a
    // `.hud-cell-lbl` line on top, the read below) from `docs/top_hud_v2.html`,
    // but Credits/Integrity/Wave now build into the LEFT HUD (`this.leftContainer`)
    // and Score/Multiplier into the TOP bar (`this.container`) — one material,
    // two docks. The data wiring is unchanged; only WHERE each cell mounts moved.

    // ── D-334 (owner decision) — the status panel is now ONE-LINE-per-read:
    //    a `.hud-row` (label left, value right-aligned) per cell, so the panel
    //    reads as a tight, calm, symmetric grid instead of stacked two-row
    //    cells. Numbers land on a shared right edge (flush with the relocated
    //    LOADOUT block's own right-aligned edge, D-333) for "aus einem Guss"
    //    symmetry. ──────────────────────────────────────────────────────────
    // ── CREDITS (left HUD) — `CREDITS` label + the gold `¢ N` read, ONE line. ─
    const creditsCell = this.createSegment("hud-credits", this.leftContainer);
    creditsCell.classList.add("hud-cell", "hud-credits-cell");
    creditsCell.style.setProperty("--hud-cl", RARITY.legendary.mid);
    const creditsRow = document.createElement("span");
    creditsRow.className = "hud-row";
    const creditsLbl = document.createElement("span");
    creditsLbl.className = "hud-cell-lbl";
    creditsLbl.textContent = t("hud.creditsLabel");
    this.creditsEl = document.createElement("span");
    this.creditsEl.className = "hud-credits-val hud-mono";
    // BL-140 a11y — labelled, but NOT a live region: credits tick up on every
    // kill, so an aria-live here would spam AT through a wave. Integrity (leaks)
    // and Wave (once per wave) are the discrete reads we announce instead.
    this.creditsEl.setAttribute("aria-label", t("hud.creditsLabel"));
    creditsRow.append(creditsLbl, this.creditsEl);
    creditsCell.appendChild(creditsRow);

    // ── INTEGRITY (left HUD, `--cl:green`) — `INTEGRITY` + `♥ N` on ONE line,
    //    the 10-segment CSS lit-cell bar on the row below. ────────────────────
    this.maxLives = Math.max(1, gs.lives);
    const integCell = this.createSegment("hud-lives", this.leftContainer);
    integCell.classList.add("hud-cell", "hud-integ-cell");
    integCell.style.setProperty("--hud-cl", COLORS.SENTINEL_GREEN);
    const integRow = document.createElement("span");
    integRow.className = "hud-row";
    const integLblTxt = document.createElement("span");
    integLblTxt.className = "hud-cell-lbl";
    integLblTxt.textContent = t("hud.integrity");
    this.integValEl = document.createElement("span");
    this.integValEl.className = "hud-integ-val hud-mono";
    // BL-140 a11y — integrity changes on a leak/heal (discrete), so announce it.
    this.integValEl.setAttribute("aria-live", "polite");
    this.integValEl.setAttribute("aria-label", t("hud.integrity"));
    integRow.append(integLblTxt, this.integValEl);
    this.integSegEl = document.createElement("div");
    this.integSegEl.className = "hud-bar-segs hud-integ-bar";
    integCell.append(integRow, this.integSegEl);

    // ── WAVE (left HUD, `--cl:amber`) — D-335 (owner defect fix on the D-334
    //    redesign): the crisp `WAVE n / 35` read and the persistent ACT marker
    //    now share ONE line — `WAVE 7 / 35 · I` — "aufgeräumt in eine Zeile"
    //    (the owner's own words). The 12-segment heat bar (green→brass→red)
    //    rides its OWN row below, alone (no longer sharing with the act marker
    //    — that pairing is what pushed the act text onto a second, truncating
    //    line at the phone tier). The act marker's SUBTITLE ("· THE PERIMETER"
    //    / "· DER PERIMETER") is DROPPED from `hud.act.N` (translations.ts) —
    //    it was atmosphere, not in-combat information, and for Act II/III it
    //    is already presented in full at the act-door transition card
    //    (`ActCard.ts`, `act.card.2/3.title` — the exact same "ACT II — HER
    //    SIGNAL" text). Act I never gets a door card (the run starts there,
    //    S130), so "THE PERIMETER" had no other home and is retired outright.
    //    D-335 CORRECTION (docs/decisions.md, amended): re-measured, dropping
    //    the subtitle alone was NOT enough to keep the WORD ("ACT III"/
    //    "AKT III") inside this line at EVERY shipped configuration — a real
    //    Chromium sweep (EN+DE, waves 1-35, the owner's real device, the true
    //    landscape-max scale, AND the real iPad rail floor) showed the WORD
    //    overflows somewhere in that range for every act, but the owner
    //    explicitly asked for the WORD ("ACT I", not a bare numeral), so it
    //    is no longer trimmed unconditionally. Instead {@link refreshActBadge}
    //    renders the WORD first and falls back to the bare Roman numeral only
    //    if THIS device's real, live layout can't fit it — never a formula,
    //    always a measurement (the same "ground truth over formula" the
    //    original D-335 vertical-budget guard used, now applied live instead
    //    of pre-baked). The marker's fixed position + its per-act colour
    //    already say "this is the act marker" for the numeral-only fallback
    //    case. The procedural flame canvas that used to sit behind this cell
    //    is RETIRED (owner decision; superseded D-323). ────────────────────
    this.waveEl = this.createSegment("hud-wave", this.leftContainer);
    this.waveEl.classList.add("hud-cell", "hud-wave-cell");
    this.waveEl.style.setProperty("--hud-cl", COLORS.AMBER_WARN);
    const waveRow = document.createElement("span");
    waveRow.className = "hud-row hud-row--end";
    this.waveNumEl = document.createElement("span");
    this.waveNumEl.className = "hud-wave-num";
    // BL-140 a11y — wave changes once per wave (discrete) → polite announce.
    this.waveNumEl.setAttribute("aria-live", "polite");
    this.waveMaxEl = document.createElement("span");
    this.waveMaxEl.className = "hud-wave-max";
    this.waveMaxEl.textContent = t("hud.waveMax", { value: FINAL_WAVE });
    // D-335 — the "·" separator between the wave read and the act marker; a
    // decorative divider, not content (aria-hidden — the act-change itself is
    // still announce()d for a11y via the ActCard door, S125).
    const waveSep = document.createElement("span");
    waveSep.className = "hud-wave-sep";
    waveSep.textContent = "·";
    waveSep.setAttribute("aria-hidden", "true");
    // BL-121 — the persistent act marker: a compact, glance-readable qualifier
    // NOW living on the wave read's own line (D-335), colour-stepped per act
    // (green → her magenta → her violet) so a screenshot from any wave answers
    // "which act?". Static ⇒ cheap + reduced-motion-safe. Its CONTENT (word
    // vs bare numeral) is decided live by {@link refreshActBadge} — never
    // truncated (no overflow/text-overflow anywhere on this cell).
    this.actBadgeEl = document.createElement("span");
    this.actBadgeEl.className = "hud-act-badge";
    waveRow.append(this.waveNumEl, this.waveMaxEl, waveSep, this.actBadgeEl);
    this.waveRowEl = waveRow;
    this.waveEl.appendChild(waveRow);
    // D-335 correction — re-measure the act badge's fit on any resize (a
    // rail-width / chrome-scale change from an orientation flip can flip
    // whether the WORD form still fits), independent of the top bar's own
    // priority-collapse observer below.
    if (typeof ResizeObserver !== "undefined") {
      this.waveRowResizeObs = new ResizeObserver(() =>
        this.refreshActBadge(actForWave(this.runtimeState.wave)),
      );
      this.waveRowResizeObs.observe(this.waveEl);
    }
    const waveBarRow = document.createElement("div");
    waveBarRow.className = "hud-wave-bar-row";
    this.waveSegEl = document.createElement("div");
    this.waveSegEl.className = "hud-bar-segs hud-wave-bar";
    waveBarRow.append(this.waveSegEl);
    this.waveEl.appendChild(waveBarRow);

    // ── SCORE (mockup `.scorecell` `--cl:cyan`) — `SCORE n` + WORLD-LEAD flag,
    //    the `.race` track (fill + the `#1` target marker), the YOU·vs meta. ──
    this.scoreEl = this.createSegment("hud-score");
    this.scoreEl.classList.add("hud-cell", "hud-score-cell");
    this.scoreEl.style.setProperty("--hud-cl", COLORS.ICE_CYAN);
    const scoreLbl = document.createElement("span");
    scoreLbl.className = "hud-cell-lbl";
    this.scoreNumEl = document.createElement("span");
    this.scoreNumEl.className = "hud-score-num";
    this.scoreNumEl.textContent = t("hud.score", { value: 0 });
    this.scoreLeadFlag = document.createElement("span");
    this.scoreLeadFlag.className = "hud-lead-flag";
    this.scoreLeadFlag.textContent = t("hud.worldLead");
    this.scoreLeadFlag.style.display = "none";
    scoreLbl.appendChild(this.scoreNumEl);
    scoreLbl.appendChild(this.scoreLeadFlag);
    this.scoreEl.appendChild(scoreLbl);
    const race = document.createElement("div");
    race.className = "hud-race";
    this.raceFillEl = document.createElement("div");
    this.raceFillEl.className = "hud-race-fill";
    this.raceRecEl = document.createElement("div");
    this.raceRecEl.className = "hud-race-rec";
    race.appendChild(this.raceFillEl);
    race.appendChild(this.raceRecEl);
    this.scoreEl.appendChild(race);
    const scoreMeta = document.createElement("span");
    scoreMeta.className = "hud-score-meta";
    const scoreYou = document.createElement("span");
    scoreYou.className = "hud-score-you";
    scoreYou.textContent = t("hud.you");
    this.scoreVsEl = document.createElement("span");
    this.scoreVsEl.className = "hud-score-vs";
    scoreMeta.appendChild(scoreYou);
    scoreMeta.appendChild(this.scoreVsEl);
    this.scoreEl.appendChild(scoreMeta);

    // ── MULT (mockup `.multcell` `--cl:amber`, left-aligned) — `MULT` label,
    //    then a big ×N + a column with the thin combo-window timer + the
    //    breakdown. Always visible (×1 at rest). ──────────────────────────────
    this.multCell = this.createSegment("hud-mult");
    this.multCell.classList.add("hud-cell", "hud-mult-cell");
    this.multCell.style.setProperty("--hud-cl", COLORS.AMBER_WARN);
    const multLabel = document.createElement("span");
    multLabel.className = "hud-cell-lbl";
    multLabel.textContent = t("hud.mult");
    const multRow = document.createElement("span");
    multRow.className = "hud-mult-row";
    this.multValEl = document.createElement("span");
    this.multValEl.className = "hud-mult-val hud-mono";
    this.multValEl.textContent = "×1";
    const multRight = document.createElement("span");
    multRight.className = "hud-mult-right";
    const multTimer = document.createElement("span");
    multTimer.className = "hud-mult-timer";
    this.multTimerFill = document.createElement("span");
    this.multTimerFill.className = "hud-mult-timer-fill";
    multTimer.appendChild(this.multTimerFill);
    this.multBrkEl = document.createElement("span");
    this.multBrkEl.className = "hud-mult-brk";
    this.multBrkEl.textContent = t("hud.multIdle");
    multRight.appendChild(multTimer);
    multRight.appendChild(this.multBrkEl);
    multRow.appendChild(this.multValEl);
    multRow.appendChild(multRight);
    this.multCell.appendChild(multLabel);
    this.multCell.appendChild(multRow);
    // CR-5: explain how the MULT works on hover (reuses the shared InfoTooltip).
    this.multCell.addEventListener("mouseenter", () =>
      this.multTooltip.show(this.multCell, {
        name: t("hud.mult.tip.name"),
        accent: COLORS.AMBER_WARN,
        tag: "SCORE",
        impact: t("hud.mult.tip.impact"),
        body: [
          t("hud.mult.tip.how1"),
          t("hud.mult.tip.how2"),
          t("hud.mult.tip.how3"),
        ],
      }),
    );
    this.multCell.addEventListener("mouseleave", () => this.multTooltip.hide());

    this.endlessEl = this.createSegment("hud-endless");
    this.endlessEl.style.display = "none";
    this.synergyEl = this.createSegment("hud-synergy");
    this.synergyEl.style.display = "none";
    this.speedEl = this.createSegment("hud-speed");
    this.speedEl.style.display = "none";

    // ── BL-63 controls — the ⚙ gear (→ settings modal); only Speed inline ─────
    const controls = document.createElement("div");
    controls.className = "hud-controls";

    // Speed is the ONLY inline control (toggled in-play). Plain `1X` glyph
    // (owner pref — no play-icon).
    this.ffBtn = document.createElement("button");
    this.ffBtn.className = "hud-btn hud-btn--warn hud-ff-btn";
    this.ffBtn.textContent = t("hud.speed.1x");
    this.ffBtn.style.display = "none";
    this.ffBtn.addEventListener("click", () => this.toggleFastForward());
    controls.appendChild(this.ffBtn);

    // BL-130 / spec 2026-pause-settings-overlays AC-4+AC-5 — the ⚙ button.
    // The BUTTON is unchanged (same id, same classes, same shared `.hud-gear-btn`
    // material from `gearMenuStyles`, so the in-game gear still looks exactly
    // like the Boot / Pre-Run ones). What changed is what it OPENS: the 208px
    // right-anchored `GearMenu` dropdown is gone, replaced by the centered
    // settings modal below. `GearMenu` itself stays for Boot / Pre-Run.
    // O-11a is untouched: opening the surface pauses the run (reusing the same
    // enterPause/exitPause the ESC path uses), closing it resumes.
    // 2026-08-11 — the PAUSE control, and it exists because of a real
    // regression rather than a wish for more chrome.
    //
    // The pause modal had exactly TWO entry points: the Escape key
    // (`game/inputHandlers.ts:434`) and iOS backgrounding (`main.ts:638`). An
    // iPad has no Escape key. This spec moved RUN NEU STARTEN and ZURÜCK ZUM
    // MENÜ out of the old dropdown and INTO that modal — so on the project's
    // stated focus platform both actions became unreachable by finger. Every
    // gate stayed green because every gate here is a keyboard/DOM gate; the
    // owner found it on a device in minutes.
    //
    // It is a SECOND button rather than a change to the ⚙, deliberately. Pause
    // and options are two intents, the ⚙ → settings path is an owner-approved
    // criterion with its own tests (AC-4/AC-5, AC-16 entry path 2), and the
    // settings panel could not have absorbed the two actions anyway: measured
    // 2026-08-11 it has 15 design px of headroom on an iPhone SE and one more
    // row needs ~61.
    this.pauseBtn = document.createElement("button");
    this.pauseBtn.type = "button";
    this.pauseBtn.id = "hud-pause";
    this.pauseBtn.className = "hud-gear-btn hud-btn";
    this.pauseBtn.textContent = "❚❚";
    this.pauseBtn.title = t("hud.pauseMenu");
    this.pauseBtn.setAttribute("aria-label", t("hud.pauseMenu"));
    this.pauseBtn.setAttribute("aria-haspopup", "dialog");
    this.pauseBtn.addEventListener("click", (e) => {
      e.stopPropagation();
      this.openPauseMenu();
    });
    controls.appendChild(this.pauseBtn);

    // BL-130 / spec 2026-pause-settings-overlays AC-4+AC-5 — the ⚙ button.
    this.gearBtn = document.createElement("button");
    this.gearBtn.type = "button";
    this.gearBtn.id = "hud-gear";
    this.gearBtn.className = "hud-gear-btn hud-btn";
    this.gearBtn.textContent = "⚙";
    this.gearBtn.title = t("hud.gear");
    this.gearBtn.setAttribute("aria-label", t("hud.gear"));
    // `dialog`, not `true`/`menu`: what opens is a modal dialog now.
    this.gearBtn.setAttribute("aria-haspopup", "dialog");
    this.gearBtn.setAttribute("aria-expanded", "false");
    this.gearBtn.addEventListener("click", (e) => {
      e.stopPropagation();
      this.toggleSettings();
    });
    controls.appendChild(this.gearBtn);

    this.container.appendChild(controls);

    parent.appendChild(this.container);
    // The mixer (the SFX/MUSIC buses) is reused, NOT rebuilt (AC-10): ONE
    // createAudioMixer per HUD, whose element is handed to the settings modal
    // and stays mounted across every open/close, so volume + mute values cannot
    // drift. The HUD never reads or writes an audio value itself.
    this.audioMixer = createAudioMixer(eventBus, audioInit);
    this.settings = new SettingsOverlay(
      overlayHost ?? parent,
      this.audioMixer.el,
      {
        onOpenLibrary: () => this.onOpenLibrary?.(),
        onToggleMuteAll: () => eventBus.emit("audio.toggleMaster"),
        // OQ-1 = (a): ÜBERNEHMEN, ZURÜCK and the backdrop all just close.
        onClose: () => this.closeSettings(),
      },
    );

    // BL-140 — dock the left HUD into the left column (`#status-row`). Headless /
    // tests pass no host → it rides under `parent` so its reads stay queryable.
    (statusHost ?? parent).appendChild(this.leftContainer);
    // S98 — paint the static segment shells once (the discrete CSS `.hud-seg`
    // cells the signed mockup uses). `update()` / `refreshScoreRace()` below just
    // toggle `.hud-seg-on` + the per-cell colour — no canvas, render-only.
    this.renderSegBar(this.waveSegEl, WAVE_SEGMENTS, 0, (i, n) =>
      waveHeatColor(i / (n - 1)),
    );
    this.syncMuteAll(false);

    // BL-63 — the responsive priority-collapse observes the bar's OWN width and
    // toggles the narrow/tight/micro classes (the core numbers never vanish).
    if (typeof ResizeObserver !== "undefined") {
      this.resizeObs = new ResizeObserver(() =>
        this.applyResponsive(this.container.clientWidth),
      );
      this.resizeObs.observe(this.container);
    }

    // BL-37 — register the HUD bar as a lit panel on the engine-material
    // UI-chrome canvas (no-op until the canvas is mounted in bootstrap).
    this.unregChrome = uiChrome.register(this.container, { intensity: 1.1 });
    // BL-140 — register the left HUD at the same intensity so top + left light
    // with one material (one premium frame).
    this.unregChromeLeft = uiChrome.register(this.leftContainer, {
      intensity: 1.1,
    });

    // BL-61/BL-62 — the commentary TV is the PERMANENT bottom-left fixture,
    // docked in-flow inside the LEFT column (#comms-dock), NOT a floating
    // #ui-layer overlay (the signed 3-column composition). It is decorative +
    // read-only: VERA & FENRIR call the game live, driven by already-emitted
    // game events through the comms arbiter — never the other way around. The
    // TV is one `MascotSurface`; the arbiter (channel-switch/cooldowns) is
    // unchanged — only WHERE it docks moved.
    if (commsDock) {
      this.commentaryTV = new CommentaryTV({
        operatorInitials: this.operatorInitials,
        className: "hud-commentary-tv",
      });
      commsDock.appendChild(this.commentaryTV.el);
      this.comms = new CommsDirector({
        surface: this.commentaryTV,
        getLives: () => this.runtimeState.lives,
        onPopOutBeat,
      });
    }

    this.update(gs);
    this.subscribe(eventBus, gs);
  }

  /** Set the operator-of-record initials shown on the commentary TV (point 4). */
  setOperatorInitials(initials: string): void {
    this.operatorInitials = initials || DEFAULT_INITIALS;
    this.commentaryTV?.setOperatorInitials(this.operatorInitials);
  }

  /**
   * Route the swap-screen commander reaction through the comms arbiter (BL-8)
   * so the operator speaks on one channel. Falls back to the standalone picker
   * only when the HUD has no comms portrait (headless / no #ui-layer).
   */
  narrateCardReaction(card: Card): string {
    return this.comms?.narrateCardReaction(card) ?? commsLine(pickCommanderQuip(card));
  }

  /** Read-only rivalry tone for the corruption layer (S126) — "even" when the
   *  HUD runs headless (no comms dock). Pure read, never drives anything. */
  rivalryTone(livesLeft: number): "ascendant" | "rattled" | "even" {
    return this.comms?.rivalryTone(livesLeft) ?? "even";
  }

  /** D-331 — BossArrival's landing seam on the TV (see CommsDirector for the
   *  priority/hold contract). A no-op headless (no comms dock); BossArrival
   *  itself still announce()s for a11y regardless. */
  announceBossArrival(title: string, name: string): void {
    this.comms?.announceBossArrival(title, name);
  }

  /** D-331 — the generic grid-banner→TV landing seam for STORY/NARRATION/
   *  PRAISE/STATUS text that used to be a centred `GameBanner` (wave-clear,
   *  payload confirmations, combo milestones, breach narration, …). A no-op
   *  headless (no comms dock). `priority` is one of `commsContent.PRIORITY`
   *  (0-4); out-of-range values are clamped by `CommsDirector`. */
  announceStatus(text: string, priority?: number): void {
    if (priority === undefined) {
      this.comms?.announceStatus(text);
      return;
    }
    const clamped = Math.min(4, Math.max(0, Math.round(priority))) as
      | 0
      | 1
      | 2
      | 3
      | 4;
    this.comms?.announceStatus(text, clamped);
  }

  // `setOnExit` / `setOnRestart` are GONE (spec 2026-pause-settings-overlays):
  // RESTART RUN and EXIT TO MENU left the gear for the PAUSE modal, which is
  // wired to the identical `game.restart` / `game.returnToMenu` events directly
  // in main.ts (`PauseOverlayActions`). The HUD no longer holds either seam, so
  // there is exactly ONE owner of each action rather than a live one and a dead
  // one.

  /** Task-012 (AC-16) — wire the settings modal's THREAT ARCHIVE row to the
   *  in-game Enemy Library overlay (main.ts owns the `EnemyLibrary` instance;
   *  the HUD only knows it needs to call something on activate, same seam as
   *  restart/exit). Deliberately does NOT close the settings modal — the archive
   *  is a fully opaque full-screen overlay above the modal's z-index, so the
   *  still-open, still-paused settings modal simply reappears once the archive
   *  is closed (no extra pause/resume bookkeeping needed). */
  setOnOpenLibrary(cb: () => void): void {
    this.onOpenLibrary = cb;
  }

  /** AC-16 — hand the HUD the pause modal `main.ts` owns, so the two menus can
   *  SWAP (never stack). Same injection seam as {@link setOnOpenLibrary}. */
  setPauseSurface(surface: PauseSurface): void {
    this.pauseSurface = surface;
  }

  /** Open/close the settings modal from the ⚙ (spec AC-4). The pause coupling
   *  below is REUSED verbatim, not rewritten — only the surface moved. */
  private toggleSettings(): void {
    if (this.settings.visible) this.closeSettings();
    else this.openSettings();
  }

  /**
   * Raise the PAUSE surface from the HUD's own control — the touch counterpart
   * of the Escape path, and the reason RUN NEU STARTEN / ZURÜCK ZUM MENÜ are
   * reachable on a device at all (see the button's construction).
   *
   * Deliberately NOT a toggle. While either modal is up, the shell's full-frame
   * backdrop sits at `UI_Z.OVERLAY` over a HUD that stacks at `z-index: auto`,
   * so this button cannot receive a second click — a toggle branch here would
   * be unreachable code pretending to be a safety net. Dismissal belongs to the
   * surfaces themselves (WEITER, the backdrop, Escape), which already own it.
   *
   * Emits the same pair as `game/inputHandlers.ts`: the phase change AND the
   * event, because `main.ts` shows the overlay off the event and `syncPauseState`
   * off the same subscription. Guarded on phase so it can never pause a run that
   * is not running (menus, draft, run-end).
   */
  private openPauseMenu(): void {
    if (this.settings.visible || this.pauseSurface?.visible === true) return;
    const phase = this.runtimeState.phase;
    if (phase !== PHASE_BUILD && phase !== PHASE_COMBAT) return;
    enterPause(this.runtimeState);
    this.bus.emit("game.paused");
  }

  /**
   * Open the settings modal — from the HUD ⚙ **or** from the ⚙ inside the pause
   * modal (`main.ts` wires `PauseOverlayActions.onOpenSettings` straight to
   * this, which is why it is public).
   *
   * **The entry path is not a parameter, it is a fact on screen** (AC-16): if
   * the pause modal is up when settings opens, this trip is a SWAP and `ZURÜCK`
   * must come back to it; if it is not, settings was opened from the running
   * game and `ZURÜCK` closes and resumes. Reading the surface instead of taking
   * a flag from the caller means the two ways to reach the same swap — the pause
   * modal's ⚙, and tabbing to the HUD ⚙ while the pause modal is up (nothing
   * traps focus) — cannot disagree about where "back" goes.
   *
   * Taking the pause modal down here is also what makes AC-7 structural rather
   * than lucky: the settings modal is only ever shown through this method, and
   * this method never leaves the other one displayed.
   *
   * The run does not change phase across the swap: it is already `PHASE_PAUSED`,
   * so {@link onGearOpen} sees `this.paused` and returns without claiming the
   * pause — which is exactly what makes {@link closeSettings}'s `onGearClose`
   * a no-op on the way back.
   */
  openSettings(): void {
    if (this.settings.visible) return;
    this.settingsFromPause = this.pauseSurface?.visible === true;
    if (this.settingsFromPause) this.pauseSurface?.hide();
    this.settings.show();
    this.gearBtn.setAttribute("aria-expanded", "true");
    this.gearBtn.classList.add("hud-gear-open");
    this.onGearOpen();
  }

  /** ÜBERNEHMEN / ZURÜCK / backdrop / Escape all land here (AC-4, OQ-1 = a).
   *  Where "back" goes is the entry path {@link openSettings} recorded. */
  private closeSettings(): void {
    if (!this.settings.visible) return;
    this.settings.hide();
    this.gearBtn.setAttribute("aria-expanded", "false");
    this.gearBtn.classList.remove("hud-gear-open");
    // Runs on BOTH paths so the pause bookkeeping has exactly one owner. On the
    // swap path it is a no-op by construction (`pausedByGear` is false — the
    // gear did not cause that pause), which is precisely why the run stays in
    // PHASE_PAUSED between the two surfaces.
    this.onGearClose();
    if (this.settingsFromPause) {
      this.settingsFromPause = false;
      this.pauseSurface?.show();
      // Focus follows the swap back to the affordance that opened settings —
      // the same "return focus where it came from" rule the gear path below
      // applies, and without it a keyboard `ZURÜCK` would leave focus on a
      // button that is now hidden.
      document.getElementById("pause-settings-btn")?.focus();
      return;
    }
    this.gearBtn.focus();
  }

  /** O-11a — opening the gear pauses the run (unless something else, e.g. ESC,
   *  already did). Deliberately does NOT emit `game.paused` — that event also
   *  drives the full-screen `PauseOverlay` (main.ts), which would render on
   *  top of (and hide) the settings modal; the settings modal IS the pause
   *  surface on this path, so only the actual phase change is reused, not the
   *  event that shows the other overlay. (The deliberate swap between the two
   *  modals is spec AC-16 / Task-004, not this seam.) */
  private onGearOpen(): void {
    if (this.paused) return; // already paused (e.g. ESC) — gear didn't cause it
    this.paused = true;
    this.pausedByGear = true;
    enterPause(this.runtimeState);
  }

  /** O-11a — closing the gear resumes, but only the pause the GEAR itself
   *  caused; if the run was already paused externally (ESC) when the gear was
   *  opened, closing the gear must not resume it out from under that. */
  private onGearClose(): void {
    if (!this.pausedByGear) return;
    this.pausedByGear = false;
    this.paused = false;
    exitPause(this.runtimeState);
  }

  /** Sync pause state when pause is triggered externally (ESC key / the
   *  native-shell background pause) — keeps the gear's own pause bookkeeping
   *  honest so it never double-pauses or resumes a pause it didn't cause. */
  syncPauseState(paused: boolean): void {
    this.paused = paused;
    if (!paused) this.pausedByGear = false;
  }

  /** Enable fast-forward button (called on every run start) */
  enableFastForward(gameLoop: GameLoop): void {
    this.gameLoop = gameLoop;
    this.ffBtn.style.display = "inline-block";
  }

  /** Toggle fast-forward between 1× and 2× */
  toggleFastForward(): void {
    if (!this.gameLoop) return;
    this.ffActive = !this.ffActive;
    this.gameLoop.timeScale = this.ffActive ? 2.0 : 1.0;
    this.ffBtn.textContent = this.ffActive
      ? t("hud.speed.2x")
      : t("hud.speed.1x");
    this.ffBtn.classList.toggle("hud-ff-active", this.ffActive);
    this.speedEl.textContent = ">> 2×";
    this.speedEl.style.display = this.ffActive ? "inline-block" : "none";
  }

  /** Reset fast-forward to 1× (call on wave end) */
  resetFastForward(): void {
    if (!this.gameLoop) return;
    this.ffActive = false;
    this.gameLoop.timeScale = 1.0;
    this.ffBtn.textContent = t("hud.speed.1x");
    this.ffBtn.classList.remove("hud-ff-active");
    this.speedEl.style.display = "none";
  }

  /** S64-F06: delegate run-state reset to CommsDirector */
  resetComms(): void {
    this.comms?.resetRunState();
  }

  /** Reflect the ECHOED master-mute state on the settings modal's ALLES STUMM
   *  row. Unchanged contract (the HUD never owns the value, it mirrors the
   *  engine's `audio.masterMuteChanged`); only the presentation moved from the
   *  dropdown's `◻`/`◼` glyph to the mockup's filled square. */
  private syncMuteAll(muted: boolean): void {
    this.settings.syncMuteAll(muted);
  }

  /**
   * Value-flash a counter: a crisp DOM kick + the engine-material chrome glint
   * (BL-37 / §6.8 "value-change is felt"). Reduced-motion → instant: the value
   * is already set by the caller; we just skip the animation.
   */
  private flashValue(el: HTMLElement): void {
    if (isReducedMotion()) return;
    uiChrome.flashValue(el);
    el.classList.remove("hud-flash");
    // Force reflow so re-adding the class restarts the transition.
    void el.offsetWidth;
    el.classList.add("hud-flash");
    const prior = this.flashTimers.get(el);
    if (prior) clearTimeout(prior);
    this.flashTimers.set(
      el,
      setTimeout(() => el.classList.remove("hud-flash"), UI_DURATION.flash),
    );
  }

  /**
   * D-335 correction (docs/decisions.md, amended) — the owner asked for the
   * WORD ("ACT I", not a bare numeral). A real-Chromium sweep (EN+DE, waves
   * 1-35, the owner's real device, the true landscape-max scale, and the
   * real iPad rail floor) showed the WORD overflows the wave row somewhere
   * in that range for every act — but not at every configuration — so a
   * single static string per locale can't be right either way (always the
   * word breaks somewhere; always the numeral drops the owner's explicit
   * ask everywhere it didn't have to). This renders the WORD first and
   * measures THIS device's actual, live layout against the cell's real
   * available width; only if the word doesn't fit HERE does it fall back to
   * the bare numeral. Never an ellipsis, never a second line — same "ground
   * truth over formula" philosophy as the original D-335 vertical-budget
   * guard, just applied live (per device) instead of pre-baked into a single
   * shipped string.
   *
   * The natural-width read needs a small trick: `.hud-row`'s `width: 100%`
   * is its CROSS-axis size inside the `.hud-wave-cell` COLUMN flex container
   * — a hard cap the row's box can never exceed — while its own CHILDREN
   * (`.hud-wave-num`/`.hud-wave-max`, no explicit flex-shrink) sit on the
   * row's own ROW-axis and silently SHRINK to absorb any deficit instead of
   * ever overflowing the row. `row.scrollWidth` therefore always reports the
   * row's own (capped) width, never the true natural content width — so it
   * cannot detect "doesn't fit" at all. Lifting the cross-axis cap to
   * `max-content` for one synchronous read (then restoring it — no paint
   * happens in between) gets the TRUE natural width instead.
   *
   * D-335 correction #2 (docs/decisions.md, amended) — coordinate-system fix.
   * `#game-container` carries `transform: scale(s)` (the stage fit), so
   * `getBoundingClientRect()` returns REAL, POST-TRANSFORM viewport px, while
   * `clientWidth`/the computed padding used for `available` are LAYOUT
   * (design-space, PRE-transform) px — the two were being compared directly,
   * which under-reports the natural width by `1/s` and made the fit check
   * always say "fits" (an independent measurement at 852×393, stage scale
   * 0.436667, found `rect.width` 98.69 real px vs. `offsetWidth` 226 layout
   * px — a ≈2.29× under-read). `clientLenToStage` (the SAME real→design
   * division `SkillDeckBar.updateIslandClearance` already uses, `utils/
   * stage.ts`) converts the rect read into the SAME coordinate system
   * `available` is already in before comparing them.
   */
  private refreshActBadge(act: ActIdentity): void {
    const word = `${t("hud.actPrefix")} ${t(act.key)}`;
    this.actBadgeEl.textContent = word;
    const prevRowWidth = this.waveRowEl.style.width;
    this.waveRowEl.style.width = "max-content";
    const naturalWidthReal = this.waveRowEl.getBoundingClientRect().width;
    this.waveRowEl.style.width = prevRowWidth;
    const naturalWidth = clientLenToStage(naturalWidthReal);
    const cellStyle = getComputedStyle(this.waveEl);
    const available =
      this.waveEl.clientWidth -
      parseFloat(cellStyle.paddingLeft || "0") -
      parseFloat(cellStyle.paddingRight || "0");
    // clientWidth is 0 in a not-yet-laid-out / detached environment (jsdom
    // has no layout engine at all) — skip the live measurement rather than
    // false-fallback to the numeral on every headless render.
    if (available > 0 && naturalWidth > available) {
      this.actBadgeEl.textContent = t(act.key);
    }
  }

  /** BL-232 — the per-wave "GO" ignite: a one-shot, dramatic pop of the WAVE
   *  number (stronger than the routine value-flash), fired on `wave.started` and
   *  synced to the board data-flow surge. Reduced-motion safe (no-op). */
  igniteWave(): void {
    if (isReducedMotion()) return;
    const el = this.waveNumEl;
    if (!el) return;
    el.classList.remove("hud-wave-igniting");
    // Force reflow so re-adding the class restarts the one-shot animation.
    void el.offsetWidth;
    el.classList.add("hud-wave-igniting");
  }

  update(gs: RuntimeState): void {
    // D-335 correction — keep the live reference current so the resize
    // observer's live re-measure (refreshActBadge) always reads the ACTUAL
    // current wave, not a stale constructor-time snapshot.
    this.runtimeState = gs;
    this.creditsEl.textContent = t("hud.credits", { value: gs.gold });
    this.waveNumEl.textContent = t("hud.wave", { value: gs.wave });
    // Integrity (BL-56 → S98 mockup `.seg`): a heal can raise the cap; the
    // depleting lit cells + the state shift (green → warn amber → danger red,
    // ALL from the central palette) carry the "mainframe is taking damage"
    // stakes. The crisp `♥ N` value rides clean above (no boxed label).
    this.maxLives = Math.max(this.maxLives, gs.lives);
    this.integValEl.textContent = t("hud.lives", { value: gs.lives });
    const ratio = this.maxLives > 0 ? gs.lives / this.maxLives : 0;
    const integCol = integrityColor(integrityState(ratio));
    this.renderSegBar(
      this.integSegEl,
      INTEGRITY_SEGMENTS,
      ratio,
      () => integCol,
    );
    // Flash only the counters that actually changed (skip the initial paint).
    if (!Number.isNaN(this.prevGold) && gs.gold !== this.prevGold)
      this.flashValue(this.creditsEl);
    const waveChanged = gs.wave !== this.prevWave;
    if (!Number.isNaN(this.prevWave) && waveChanged)
      this.flashValue(this.waveNumEl);
    this.prevGold = gs.gold;
    this.prevWave = gs.wave;
    // BL-121/D-335 correction — re-measure the act badge whenever the act OR
    // the wave itself changes (a 1-digit -> 2-digit wave alone can flip
    // whether the WORD form still fits, even with the act unchanged) — NOT
    // on every update() call (gold ticks on every kill; a forced-layout
    // width read on each one would be wasteful). The clamp keeps endless on
    // Act III.
    const act = actForWave(gs.wave);
    if (waveChanged || act.act !== this.prevAct) {
      this.actBadgeEl.style.setProperty("--act-cl", act.color);
      this.refreshActBadge(act);
      this.prevAct = act.act;
    }
    // BL-58 — `--heat` (clamp(wave/FINAL_WAVE)) still warms the readout's own
    // DOM glow (text-shadow) so the digits feel hot as the run climbs. D-334 —
    // the procedural WAVE-FIRE canvas that used to burn behind this cell is
    // RETIRED (owner decision); the glow is now the only heat cue.
    const heat = Math.min(1, Math.max(0, gs.wave / FINAL_WAVE));
    this.waveEl.style.setProperty("--heat", heat.toFixed(3));
    this.waveEl.classList.toggle("hud-wave-overload", gs.wave > FINAL_WAVE);
    // BL-63 → S98 — the 12-segment heat bar fills toward FINAL_WAVE; each lit
    // cell is pre-coloured green→brass→red by position (the mockup `heat()`
    // ramp). Overload (endless) pins it full.
    const waveFrac = Math.min(
      1,
      (gs.wave > FINAL_WAVE ? FINAL_WAVE : gs.wave) / FINAL_WAVE,
    );
    this.renderSegBar(this.waveSegEl, WAVE_SEGMENTS, waveFrac, (i, n) =>
      waveHeatColor(i / (n - 1)),
    );
    this.endlessEl.textContent = t("hud.endlessFrozen");
    this.endlessEl.style.display = gs.isEndlessMode ? "inline-block" : "none";
  }

  /**
   * S98 — (re)paint a discrete CSS segment bar to the signed mockup's `.seg`
   * look: build the fixed cells once, then light `fillFrac` of them (`i/n <
   * fillFrac`, exactly the mockup `buildBar`) and tint each lit cell from its
   * central-palette colour. Pure render — no canvas, determinism-neutral.
   */
  private renderSegBar(
    host: HTMLElement,
    count: number,
    fillFrac: number,
    colorFn: (i: number, n: number) => string,
  ): void {
    if (host.childElementCount !== count) {
      host.replaceChildren();
      for (let i = 0; i < count; i++) {
        const s = document.createElement("div");
        s.className = "hud-seg";
        host.appendChild(s);
      }
    }
    const segs = host.children;
    for (let i = 0; i < count; i++) {
      const s = segs[i] as HTMLElement;
      const on = i / count < fillFrac;
      s.classList.toggle("hud-seg-on", on);
      if (on) {
        const c = colorFn(i, count);
        s.style.background = c;
        s.style.setProperty("--hud-sc", c);
      } else {
        s.style.background = "";
        s.style.removeProperty("--hud-sc");
      }
    }
  }

  updateScore(score: number): void {
    this.liveScore = score;
    this.scoreNumEl.textContent = t("hud.score", { value: score });
    if (!Number.isNaN(this.prevScore) && score !== this.prevScore)
      this.flashValue(this.scoreNumEl);
    this.prevScore = score;
    this.refreshScoreRace();
  }

  // ── BL-63 SCORE RACE — vs the live global #1, with a local-best fallback ────

  /**
   * Wire the score-race (from `main.ts`). Stored only; the actual `fetchTop(1)`
   * happens once per run in {@link beginScoreRace}. The local best is the
   * always-available fallback target so the bar is never empty/blocked.
   */
  attachScoreRace(cfg: ScoreRaceConfig): void {
    this.scoreRace = cfg;
  }

  /**
   * Run-start: seed the race against the local best immediately (non-blocking),
   * then resolve the live global #1 async. Any failure / disabled / empty result
   * keeps the local-best target — never throws, never shows an error.
   */
  beginScoreRace(): void {
    const cfg = this.scoreRace;
    if (!cfg) return;
    // 1) immediate local-best target (the bar always has something to race).
    this.setScoreTarget(cfg.getLocalBest(), "", false);
    // 2) the live global #1 (async; falls back silently to the local best).
    void cfg
      .fetchTopScore()
      .then((top) => {
        if (top && top.score > 0) {
          this.setScoreTarget(top.score, top.initials, true);
        }
      })
      .catch(() => {
        /* graceful — keep the local-best target */
      });
  }

  /** Set the race target (the `.race` max) + the holder + the global/local flag. */
  private setScoreTarget(
    target: number,
    holder: string,
    isGlobal: boolean,
  ): void {
    this.scoreTarget = Math.max(0, Math.round(target));
    this.scoreHolder = holder;
    this.scoreTargetIsGlobal = isGlobal;
    this.refreshScoreRace();
  }

  /** Re-paint the `.race` fill + the `#1` marker + the vs-meta + the WORLD-LEAD
   *  overflow for the live score. Pure render — called on every score change and
   *  on a target change (the signed mockup's `.race`/`.fill`/`.rec`). */
  private refreshScoreRace(): void {
    const target = this.scoreTarget;
    const lead = target > 0 && this.liveScore >= target;
    // The fill = your score ÷ the live global #1 (0..100%); in the lead the CSS
    // `.hud-score-lead` pins it full + gold. The `#1` marker sits at the target.
    const frac = target > 0 ? Math.min(1, this.liveScore / target) : 0;
    this.raceFillEl.style.width = `${(frac * 100).toFixed(1)}%`;
    if (lead !== this.inLead) {
      this.inLead = lead;
      // Beaten #1 → the fill goes gold (data-gold / CLASS-Ω) + the flag flares.
      this.scoreEl.classList.toggle("hud-score-lead", lead);
      this.scoreLeadFlag.style.display = lead ? "inline-block" : "none";
    }
    if (lead) {
      this.scoreVsEl.textContent = t("hud.youAreNumber1");
    } else if (target <= 0) {
      this.scoreVsEl.textContent = "";
    } else if (this.scoreTargetIsGlobal) {
      const holder = this.scoreHolder || t("hud.rank1");
      this.scoreVsEl.textContent = t("hud.vsRank", {
        holder,
        value: groupThousands(target),
      });
    } else {
      this.scoreVsEl.textContent = t("hud.vsPersonal", {
        value: groupThousands(target),
      });
    }
    this.scoreVsEl.title = this.scoreVsEl.textContent; // narrow-screen tooltip
  }

  // ── BL-63 MULTIPLIER TRANSPARENCY — always-visible ×N + timer + breakdown ───

  /** Wire the live combo-state provider (from `ScoreSystem` in `main.ts`). */
  attachMultiplier(source: MultiplierSource): void {
    this.multSource = source;
    this.renderMultiplier();
  }

  /** A combo tier was crossed — kick the ×N flash + ensure the timer loop runs. */
  private onComboTierUp(): void {
    this.flashValue(this.multValEl);
    this.renderMultiplier();
    this.startMultLoop();
  }

  /** The chain lapsed — settle the cell back to ×1 and stop the timer loop. */
  private onComboEnd(): void {
    this.stopMultLoop();
    this.renderMultiplier();
  }

  /** Read the live combo state and paint the ×N, the window timer + breakdown.
   *  Crisp DOM; the timer is a plain depleting bar (NOT a fill bar — §④). */
  private renderMultiplier(): void {
    const s = this.multSource?.() ?? {
      multiplier: 1,
      combo: 0,
      windowRemaining: 0,
      timeoutMult: 1,
      scoreMult: 1,
    };
    const multTxt = `×${s.multiplier}`;
    if (multTxt !== this.multValEl.textContent)
      this.multValEl.textContent = multTxt;
    this.multValEl.classList.toggle("hud-mult-hot", s.multiplier > 1);
    // The depleting combo-window timer (urgency — keep killing). Red <30%.
    const pct = Math.round(s.windowRemaining * 100);
    this.multTimerFill.style.width = `${pct}%`;
    this.multTimerFill.classList.toggle(
      "hud-mult-timer-low",
      s.windowRemaining < 0.3,
    );
    const active = s.combo > 0;
    this.multCell.classList.toggle("hud-mult-active", active);
    // The concise breakdown — the chain count + any skill boosts (≠1).
    let brk: string;
    if (!active) {
      brk = t("hud.multIdle");
    } else {
      const parts = [t("hud.chain", { value: s.combo })];
      if (s.timeoutMult !== 1) parts.push(t("hud.multWindow"));
      if (s.scoreMult !== 1) parts.push(t("hud.multBoost"));
      brk = parts.join(" · ");
    }
    if (brk !== this.prevBrk) {
      this.multBrkEl.textContent = brk;
      this.multBrkEl.title = brk; // narrow-screen tooltip when the text folds
      this.prevBrk = brk;
    }
  }

  /** The combo-window timer self-drives a throttled rAF while a chain is live.
   *  Reduced-motion → no loop (a static snapshot; the values still read). */
  private startMultLoop(): void {
    if (this.multRaf || !this.multSource) return;
    if (isReducedMotion()) {
      this.renderMultiplier();
      return;
    }
    this.multLastDraw = 0;
    const tick = (): void => {
      if (!this.multSource) {
        this.multRaf = 0;
        return;
      }
      const ts = now();
      if (ts - this.multLastDraw >= MULT_FRAME_MS) {
        this.multLastDraw = ts;
        this.renderMultiplier();
      }
      // Self-stop once the chain lapses (idle ≈ 0 between combos).
      if (this.multSource().combo > 0) {
        this.multRaf = requestAnimationFrame(tick);
      } else {
        this.multRaf = 0;
        this.renderMultiplier();
      }
    };
    this.multRaf = requestAnimationFrame(tick);
  }

  private stopMultLoop(): void {
    if (this.multRaf) cancelAnimationFrame(this.multRaf);
    this.multRaf = 0;
  }

  /** Set operator-specific accent color for HUD border. */
  setAccentColor(color: string): void {
    this.container.style.borderBottomColor = color;
  }

  destroy(): void {
    for (const unsub of this.unsubscribers) unsub();
    for (const timer of this.flashTimers.values()) clearTimeout(timer);
    this.flashTimers.clear();
    this.stopMultLoop();
    this.settings.destroy();
    this.resizeObs?.disconnect();
    this.resizeObs = null;
    this.waveRowResizeObs?.disconnect();
    this.waveRowResizeObs = null;
    this.unregChrome?.();
    this.unregChrome = null;
    this.unregChromeLeft?.();
    this.unregChromeLeft = null;
    this.audioMixer?.dispose();
    this.audioMixer = null;
    this.comms?.detach();
    this.comms = null;
    this.commentaryTV?.destroy();
    this.commentaryTV = null;
    this.multTooltip.hide();
    this.container.remove();
    this.leftContainer.remove();
  }

  private subscribe(eventBus: EventBus, gs: RuntimeState): void {
    this.unsubscribers.push(
      eventBus.on("wave.started", () => this.update(gs)),
      eventBus.on("wave.completed", () => this.update(gs)),
      eventBus.on("enemy.reachedEnd", () => {
        this.update(gs);
        // BL-256 — a leaked enemy damaging the mainframe is canvas-only (shake +
        // red flash + vignette); mirror it to the ASSERTIVE live region so a
        // screen-reader player is told a life was lost and the integrity left.
        // The polite integrity read (integValEl) can be buried under a burst of
        // wave telegraphs; the core taking damage warrants an interrupt.
        announceAssertive(t("a11y.coreHit", { value: gs.lives }));
      }),
      // Credits must read LIVE, not a frame behind. `gs.gold` is only synced
      // from `economy.currentGold` once per render frame (renderPipeline), but a
      // buy/sell/upgrade mutates the economy mid-input and fires synchronously —
      // so reading `gs.gold` here would show the value from BEFORE this very
      // transaction (the one-transaction lag). Pull the authoritative new total
      // straight off the event payload instead, then paint.
      eventBus.on("economy.earned", (d: unknown) => {
        syncGold(gs, (d as { total: number }).total);
        this.update(gs);
      }),
      eventBus.on("economy.spent", (d: unknown) => {
        syncGold(gs, (d as { remaining: number }).remaining);
        this.update(gs);
      }),
      eventBus.on("economy.bonus", (d: unknown) => {
        syncGold(gs, (d as { total: number }).total);
        this.update(gs);
      }),
      // BL-155 (S158) — the LEECH siphon: paint the credits DROP immediately (the
      // owner's #1 — the drain must be SEEN), not a frame late. `update()` flashes
      // the credits cell on the change, pairing with the in-lane amber siphon VFX.
      eventBus.on("economy.leeched", (d: unknown) => {
        syncGold(gs, (d as { total: number }).total);
        this.update(gs);
      }),
      eventBus.on("tower.placed", () => this.update(gs)),
      eventBus.on("tower.sold", () => this.update(gs)),
      eventBus.on("tower.upgraded", () => this.update(gs)),
      eventBus.on("score.changed", (d: unknown) => {
        const ev = d as { score: number; combo?: number };
        this.updateScore(ev.score);
        // A kill happened — refresh the live ×N + (re)start the timer loop if a
        // chain is running (the loop self-stops when it lapses).
        this.renderMultiplier();
        if ((ev.combo ?? 0) > 0) this.startMultLoop();
      }),
      eventBus.on("score.combo", () => this.onComboTierUp()),
      eventBus.on("score.comboEnd", () => this.onComboEnd()),
      eventBus.on("audio.masterMuteChanged", (m: unknown) =>
        this.syncMuteAll(m as boolean),
      ),
      eventBus.on("synergy.updated", () => {
        // S146/BL-140: the legacy inline synergy chip stays hidden (synergy is
        // surfaced on the board links + the tower info panel, not the HUD bar).
        this.synergyEl.style.display = "none";
      }),
    );

    this.comms?.attach(eventBus);
  }

  // ── BL-63/BL-130 — the ⚙ gear ─────────────────────────────────────────────
  //
  // `buildGearMenu` is GONE (spec `2026-pause-settings-overlays`, AC-5). It
  // populated the 208px right-anchored dropdown with a `pause.message` note,
  // `// AUDIO` (MUTE ALL + the mixer) and `// CONTROLS` (archive · restart ·
  // exit). Where each of those went:
  //   · the PAUSED note      → deleted; the pause MODAL carries the state now
  //                            (`pause.title`/`pause.subtitle`), and its last
  //                            reader gone, `pause.message` retires with it.
  //   · MUTE ALL + the mixer → `SettingsOverlay`'s `// AUDIO` section, with the
  //                            SAME `audio.toggleMaster` intent and the SAME
  //                            (reused, never rebuilt) `AudioMixer` element.
  //   · THREAT ARCHIVE       → `SettingsOverlay`'s `// SPIEL` section, same
  //                            `onOpenLibrary` seam, same `aria-label`.
  //   · RESTART · EXIT       → the PAUSE modal (Task-002), which is why the
  //                            `// CONTROLS` section disappears outright — and
  //                            with it the two untrue `R` / `Esc` keycaps
  //                            (owner decision OQ-3 = b).

  /** Priority-based progressive collapse driven by the TOP bar's OWN width: shed
   *  the score meta first (narrow), then the combo timer (tight), then the race
   *  bar (micro) — the SCORE + ×N numbers never vanish and the controls always
   *  fit. Thresholds lowered for the slimmer Score+Mult bar (S146/BL-140). */
  private applyResponsive(width: number): void {
    if (width <= 0) return; // not laid out yet (jsdom / pre-mount)
    this.container.classList.toggle("hud-narrow", width < 820);
    this.container.classList.toggle("hud-tight", width < 580);
    this.container.classList.toggle("hud-micro", width < 420);
  }

  private createSegment(
    id: string,
    host: HTMLElement = this.container,
  ): HTMLSpanElement {
    const el = document.createElement("span");
    el.id = id;
    el.className = "hud-segment";
    host.appendChild(el);
    return el;
  }

  /**
   * The HUD's ONE stylesheet.
   *
   * It embeds the shared gear CSS (BL-130) so Boot / PreRun / HUD render the
   * identical lit-CRT ⚙. Since spec `2026-pause-settings-overlays` (AC-5) it
   * embeds the BUTTON half only (`buttonOnly: true`): the in-game dropdown is
   * retired, so this sheet no longer declares the 208px right-anchored panel or
   * any of its rows — the gear opens the centered settings modal instead. Boot
   * and PreRun still inject the full sheet from the same source, unchanged.
   *
   * (That rationale lives here, not inside the emitted template below: a comment
   * in the CSS string is string CONTENT and ships in the size-gated bundle.)
   */
  private injectStyles(): void {
    if (document.getElementById("hud-styles")) return;
    const style = document.createElement("style");
    style.id = "hud-styles";
    // D-336 — the ONE shared value size for every left-rail status read
    // (CREDITS / INTEGRITY / WAVE): see the "left rail restyle" block below.
    const HUD_VALUE_PX = 18;
    style.textContent = `
      /* ════════════════════════════════════════════════════════════════════
         S98 TOP-HUD v2 — the live bar ported 1:1 from the signed
         'docs/top_hud_v2.html': flush divided cells + a two-row rhythm, the
         10-seg integrity + 12-seg heat wave, the '.race' score marker, the
         left-aligned MULT, and the '1X' + ⚙ controls. Same structure + same
         CSS ⇒ the same pixels. The SEMANTIC colours map to the CENTRAL palette
         (COLORS / RARITY — no new gameplay hex); only the dark UI chrome
         (bar/track) keeps its structural value. Render-only (Layer 4). */
      /* S146/BL-140 — TOP bar (#hud-bar) and LEFT HUD (#hud-left) share ONE
         lit-CRT panel material + ONE central-palette var set, so they read as a
         single premium frame ("aus einem Guss"). The top lays the cells out in a
         row (vertical dividers); the left stacks the SAME cells (horizontal
         dividers) — the same vocabulary rotated 90°. Colours map to the CENTRAL
         palette (COLORS / RARITY — no new gameplay hex); only the dark UI chrome
         (panel ground / track) keeps its structural value. Render-only (L4). */
      #hud-bar, #hud-left {
        /* central-palette vars — mapped to COLORS/RARITY */
        --hud-green: ${COLORS.SENTINEL_GREEN};
        --hud-green-hi: color-mix(in srgb, ${COLORS.SENTINEL_GREEN} 48%, #fff);
        --hud-green-dk: ${COLORS.SENTINEL_GREEN_DIM};
        --hud-amber: ${COLORS.AMBER_WARN};
        --hud-red: ${COLORS.ORACLE_RED};
        --hud-cyan: ${COLORS.ICE_CYAN};
        --hud-gold: ${RARITY.legendary.mid};
        --hud-gold-hi: color-mix(in srgb, ${RARITY.legendary.mid} 55%, #fff);
        --hud-gold-dk: color-mix(in srgb, ${RARITY.legendary.mid} 72%, #000);
        --hud-dim: ${COLORS.SENTINEL_GREEN_DIM};
        --hud-track: color-mix(in srgb, ${COLORS.SENTINEL_GREEN} 6%, ${PANEL.BG});
        --hud-divider: color-mix(in srgb, ${COLORS.SENTINEL_GREEN} 13%, ${PANEL.BG});
        position: relative;
        border-radius: 8px;
        background: linear-gradient(180deg, #0c140d, #080b08 60%, #06080a);
        box-shadow:
          0 0 0 1px color-mix(in srgb, ${COLORS.SENTINEL_GREEN} 24%, transparent),
          0 0 22px color-mix(in srgb, ${COLORS.SENTINEL_GREEN} 8%, transparent),
          0 18px 50px -22px #000;
        font-family: ${UI_FONT_STACK};
        font-size: ${withUiScale(UI_TYPE.numeric.size)};
        color: ${COLORS.TEXT_PRIMARY};
        user-select: none;
      }
      /* TOP bar — horizontal flush cells (the Score + Multiplier reads + ⚙). */
      #hud-bar {
        display: flex;
        align-items: stretch;
        flex-wrap: nowrap;
        /* BL-206 — the MULT cell's stacked content (the MULT label + the big ×N +
           the combo timer) is ALL --ui-scale-aware type (roleFontSize/withUiScale
           → calc(px * var(--ui-scale))), but the bar height was a FIXED px tuned at
           scale 1. At the live --ui-scale:1.25 the scaled stack outgrew the box, so
           the vertically-centered content poked above the bar's top and — the bar
           sitting flush at the stage top — the #game-container frame cropped the
           MULT label (the earlier 54→58px bump was still short). The fix: tie the
           bar height to the SAME --ui-scale knob its content uses, as a min-height
           so it can only ever GROW (never clip): byte-identical 54px at scale 1,
           ~67.5px at 1.25 — the proportional slack is preserved at every scale, and
           #hud-row (flex:0 0 auto) just reallocates the few px from #main-area. */
        min-height: calc(54px * var(--ui-scale, 1));
      }
      /* LEFT HUD — the SAME cells stacked into a tidy panel docked at the top of
         the left column (Credits · Integrity · Wave). Insets match the comms
         dock below (8px) so the column reads as one composed stack. */
      #hud-left {
        display: flex;
        flex-direction: column;
        box-sizing: border-box;
        /* BL-291 — the leading edge comes from the rail's shared gutter, not
           from this block's own margin + padding. Before, margin(8) + the
           Island padding(135.1) stacked to 143.1 while the dock below started
           at 135.1 and the loadout at 210 — three edges in one rail. */
        /* MARGIN, not padding: this block draws its own frame, so the frame —
           not just the text inside it — has to land on the shared column. With
           padding the border box ran to the rail edge (438) while the loadout
           and the dock's TV ended at 430, and the eye reads the frame. */
        margin: 8px var(--rail-pad-r) 0 var(--rail-pad-l);
        overflow: hidden;
      }
      /* CR-3/CR-5 — iPhone-only: nudge the whole left status cluster inboard so
         it clears the Dynamic Island / rounded display corner on the leading
         edge in landscape. D-335 — the nudge is now DERIVED from the real
         per-side safe-area inset (--inset-left-design, published by
         index.html's fit() script — the SAME inset ÷ live stage scale
         division SkillDeckBar.updateIslandClearance already uses for "//
         LOADOUT", D-333), not a hard-coded design px (a fixed 22px could
         render as ~9.6 REAL px on a small device, under-clearing the actual
         Island). max(22px, …) — a zero inset (desktop/iPad, no notch, or a
         rotation where the Island sits on the OTHER edge) falls back to
         exactly today's 22px, never collapses to 0. iPad/desktop never get
         .phone-tier, so they are byte-identical either way. */
      /* BL-291 — retired: the clearance now lives on #left-panel's --rail-pad-l,
         so all three rail blocks inherit ONE leading edge (see index.html). */
      .hud-mono { font-family: ${UI_FONT_STACK}; font-variant-numeric: tabular-nums; }
      /* ── the flush two-row cell: a '.hud-cell-lbl' line on top, the read below;
         'border-right' dividers give the top bar its tidy ordered rhythm. ────── */
      .hud-cell {
        position: relative;
        display: flex;
        flex-direction: column;
        justify-content: center;
        gap: 3px;
        padding: 0 14px;
        border-right: 1px solid var(--hud-divider);
        min-width: 0;
      }
      .hud-cell:last-child { border-right: none; }
      /* D-334 (owner decision — "achte auf Symmetrie, Style, Ruhe, Premium
         Look") — LEFT-HUD cells drop the horizontal divider (calm: fewer
         borders) and tighten to the shared UI_SPACE ladder (never a magic
         number): padding sm/lg, gap xxs. Each cell is now a ONE-LINE
         label+value row (.hud-row, see below) instead of the old stacked
         two-row rhythm. */
      #hud-left .hud-cell {
        border-right: none;
        padding: ${UI_SPACE.sm}px ${UI_SPACE.lg}px;
        gap: ${UI_SPACE.xxs}px;
      }
      /* One shared row primitive for every left-HUD cell: label left, value
         right — numbers land on ONE right edge (flush with the right-aligned
         LOADOUT block below, D-333) so the panel reads as a single grid. */
      .hud-row {
        display: flex;
        align-items: baseline;
        justify-content: space-between;
        gap: ${UI_SPACE.xs}px;
        width: 100%;
      }
      /* WAVE's row has no leading label (the number already reads "WAVE n") —
         push it flush right so its digits still land on the shared edge. */
      .hud-row--end { justify-content: flex-end; }
      .hud-cell-lbl {
        font-family: ${UI_FONT_STACK};
        font-size: ${roleFontSize("caption")};
        letter-spacing: 1.7px;
        color: var(--hud-cl, var(--hud-dim));
        line-height: 1;
        display: flex;
        align-items: center;
        gap: 6px;
        white-space: nowrap;
      }
      /* a base segment span (createSegment) — the flash transition lives here;
         cells override display via '.hud-cell'. */
      .hud-segment {
        white-space: nowrap;
        flex-shrink: 0;
        transition: transform ${UI_DURATION.hover}ms ${UI_EASE.snap},
          filter ${UI_DURATION.flash}ms ${UI_EASE.decay};
      }
      /* Value-flash: the changing read kicks bright then settles (the chrome
        glint is on the canvas; this is the crisp DOM half). */
      .hud-flash {
        filter: brightness(1.9) drop-shadow(0 0 6px currentColor);
        transform: scale(1.12);
      }
      @media (prefers-reduced-motion: reduce) {
        .hud-segment { transition: none; }
        .hud-flash { filter: none; transform: none; }
      }
      /* D-336 (owner rejection — "das linke HUD sieht noch nicht so clean
         aus... schriftgrößen...") — the left rail previously ran FOUR type
         sizes across three status rows (credits 23px, integrity 16px, wave
         19px, plus the 13px caption labels/act-badge/wave-max): a set of
         accidental accents, not a deliberate one. Unified to exactly TWO
         sizes for the whole rail: the shared ${roleFontSize("caption")}
         label size, and ONE value size (below) for every primary read —
         credits/integrity/wave. Credits keeps its OWN colour (gold) as the
         single deliberate accent the owner's "primary resource" reading
         calls for; it no longer also gets a bigger typeface for it. */
      /* ── CREDITS (left HUD, '--cl:gold') — 'CREDITS' label + the gold '¢ N'
         read, ONE line (D-334). ──────────────────────────────────────────── */
      #hud-credits { overflow: hidden; }
      .hud-credits-val {
        color: var(--hud-gold);
        font-size: ${withUiScale(HUD_VALUE_PX)};
        line-height: 1;
        text-shadow: 0 0 10px color-mix(in srgb, ${RARITY.legendary.mid} 50%, transparent);
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        /* O-2a — was missing a transition, so .hud-flash's filter/transform
           snapped instantly ("jitter"); see .hud-wave-num for precedent. */
        transition: filter ${UI_DURATION.flash}ms ${UI_EASE.decay},
          transform ${UI_DURATION.hover}ms ${UI_EASE.snap};
      }
      @media (prefers-reduced-motion: reduce) {
        .hud-credits-val { transition: none; }
      }
      /* ── INTEGRITY (left HUD, '--cl:green') — 'INTEGRITY ♥ N' on ONE line
         (D-334) over the 10-'.seg' bar. ──────────────────────────────────── */
      #hud-lives {
        min-width: 110px;
        overflow: visible;
      }
      .hud-integ-val { color: ${COLORS.TEXT_GLITCH}; font-size: ${withUiScale(HUD_VALUE_PX)}; line-height: 1; }
      /* ── the discrete CSS lit-cell bar (mockup '.bar'/'.seg'/'.seg.on') — the
         ONE shared visual treatment INTEGRITY and WAVE both render through
         (D-334: they read as one family, not near-identical siblings).
         D-336 — BOTH bars now stretch to the FULL row width (a '.hud-seg'
         used to be a fixed 10px cell, so the 10-segment integrity bar sat at
         a fixed 118px, LEFT-aligned under the RIGHT-aligned row above it,
         while only the wave bar had its own 'flex: 1 1 auto' override to
         span the cell — two different treatments reading as two different
         objects). Every segment is now 'flex: 1 1 0' so BOTH bars span the
         SAME width as their row (a quiet underline of the row, not a
         standalone widget), at the SAME height, in the SAME material. ──── */
      .hud-bar-segs { display: flex; gap: 2px; align-items: center; height: 14px; width: 100%; }
      .hud-seg {
        flex: 1 1 0;
        min-width: 0;
        height: 13px;
        border-radius: 1.5px;
        background: var(--hud-track);
        box-shadow: inset 0 0 0 1px rgba(255,255,255,.04);
      }
      .hud-seg-on {
        box-shadow: 0 0 5px -1px var(--hud-sc, var(--hud-green)),
          inset 0 0 0 1px rgba(255,255,255,.12);
      }
      /* ── WAVE (mockup '.wavecell' '--cl:amber') — the crisp 'WAVE n / 35'
         read over the 12-seg heat bar. D-334 (owner decision) — the procedural
         flame canvas that used to burn behind this cell is RETIRED (superseding
         the D-323 flame-octave work); '--heat' still warms the number's own
         glow below. */
      #hud-wave {
        --heat: 0;
        min-width: 120px;
      }
      .hud-wave-num {
        white-space: nowrap;
        font-variant-numeric: tabular-nums;
        font-size: ${withUiScale(HUD_VALUE_PX)};
        color: color-mix(in srgb, ${COLORS.TEXT_GLITCH} calc(46% + var(--heat) * 46%), ${COLORS.SENTINEL_GREEN});
        text-shadow:
          0 0 2px ${COLORS.BG_PRIMARY},
          0 1px 2px ${COLORS.BG_PRIMARY},
          0 0 calc(4px + var(--heat) * 10px) color-mix(in srgb, ${COLORS.AMBER_WARN} calc(var(--heat) * 90%), transparent);
        transition: color ${UI_DURATION.flash}ms ${UI_EASE.settle},
          transform ${UI_DURATION.hover}ms ${UI_EASE.snap},
          filter ${UI_DURATION.flash}ms ${UI_EASE.decay};
      }
      .hud-wave-max { color: var(--hud-dim); font-size: ${roleFontSize("caption")}; }
      /* D-335 — the bar row now carries ONLY the 12-seg heat bar (flex:1,
         grows to fill the cell). The ACT marker moved onto the wave-read's
         own line above (see waveRow in the constructor) — sharing this row
         with the bar is what pushed a variable-width act label onto a SECOND
         line at the phone tier, which then had to ellipsis-truncate mid-word
         ("ACT I · THE PE…") — the defect this decision fixes. */
      .hud-wave-bar-row {
        display: flex;
        align-items: center;
        gap: ${UI_SPACE.xs}px;
      }
      .hud-wave-bar-row .hud-bar-segs { flex: 1 1 auto; min-width: 24px; }
      /* D-335 — the WAVE cell's own row is tightened to the "hair" gap (2px,
         not the shared .hud-row 6px) — measured (real Chromium) to be the
         difference between fitting and not at the phone-tier scale ceiling
         with a 2-digit wave in DE (docs/decisions.md D-335). Scoped to THIS
         cell only — CREDITS/INTEGRITY keep the shared .hud-row rhythm. */
      .hud-wave-cell .hud-row { gap: ${UI_SPACE.hair}px; }
      /* D-335 correction (docs/decisions.md, amended) — the real iPad
         configuration (--left-panel-width at the RAIL_MIN_LEFT floor,
         176px, --chrome-scale inert at 1 outside the phone tier,
         --ui-scale always L_UI_SCALE 1.25) genuinely overflows the WAVE
         row by a measured few px even with the bare Roman-numeral act marker
         — the open question the D-335 session flagged as a "phantom"
         (narrow-rail + phone-tier chrome-scale never co-occur) did NOT
         cover this real, chrome-scale-1 combination. Scoped to THIS cell
         only (CREDITS/INTEGRITY keep the shared padding): the side padding
         tightens lg(12px)->sm(8px), reclaiming the few px needed to clear it
         — the same "scope the cut to the one cell that needs it" pattern
         the gap tightening above already uses. */
      #hud-left .hud-wave-cell { padding-left: ${UI_SPACE.sm}px; padding-right: ${UI_SPACE.sm}px; }
      /* D-335 — the wave-read separator ("·") between "/ 35" and the act
         marker; purely decorative (aria-hidden on the element itself). */
      .hud-wave-sep {
        flex: 0 0 auto;
        color: var(--hud-dim);
        font-size: ${roleFontSize("caption")};
        opacity: 0.7;
      }
      /* BL-121/D-335 (corrected, docs/decisions.md amended) — the persistent
         act marker: a compact, glance-readable qualifier sharing the WAVE
         read's own line (never a second line, never an ellipsis). Content
         (the WORD, "ACT I"/"AKT I", or the bare Roman numeral fallback) is
         decided LIVE per device by HUD.ts's refreshActBadge — a real-
         Chromium sweep (EN+DE, waves 1-35, the owner's device, the true
         landscape max, the real iPad rail floor) showed no single static
         string is safe everywhere, so this is measured, not pre-baked. The
         fixed position (always the WAVE row's trailing element) + the
         per-act colour (green → her magenta → her violet) still carry "this
         is the act marker" even when the fallback numeral ships. flex: 0 0
         auto — it never shrinks or truncates; white-space: nowrap is a
         belt-and-braces guard, not a truncation mechanism (no
         overflow/text-overflow here). Static once set ⇒ reduced-motion-safe. */
      .hud-act-badge {
        flex: 0 0 auto;
        text-align: right;
        font-size: ${roleFontSize("caption")};
        letter-spacing: 0.16em;
        line-height: 1.1;
        white-space: nowrap;
        color: var(--act-cl, ${COLORS.SENTINEL_GREEN});
        text-shadow:
          0 0 1px ${COLORS.BG_PRIMARY},
          0 0 8px color-mix(in srgb, var(--act-cl, ${COLORS.SENTINEL_GREEN}) 55%, transparent);
        transition: color ${UI_DURATION.flash}ms ${UI_EASE.settle};
      }
      @media (prefers-reduced-motion: reduce) {
        .hud-act-badge { transition: none; }
      }
      .hud-wave-num.hud-flash {
        filter: brightness(1.7) drop-shadow(0 0 7px ${COLORS.AMBER_WARN});
        transform: scale(1.12);
      }
      /* BL-232 — the per-wave "GO" ignite: a one-shot dramatic pop of the WAVE
         number, synced to the board data-flow surge. Ends back at rest. */
      .hud-wave-num.hud-wave-igniting {
        animation: hudWaveIgnite 0.5s ease-out;
      }
      @keyframes hudWaveIgnite {
        0% { transform: scale(1); filter: brightness(1); }
        35% {
          transform: scale(1.28);
          filter: brightness(2) drop-shadow(0 0 12px ${COLORS.AMBER_WARN});
        }
        100% { transform: scale(1); filter: brightness(1); }
      }
      @media (prefers-reduced-motion: reduce) {
        .hud-wave-num.hud-wave-igniting { animation: none; }
      }
      #hud-wave.hud-wave-overload .hud-wave-num {
        color: ${COLORS.TEXT_GLITCH};
        animation: hud-wave-overload 0.9s ease-in-out infinite alternate;
      }
      @keyframes hud-wave-overload {
        from { text-shadow: 0 0 3px ${COLORS.BG_PRIMARY}, 0 0 10px ${COLORS.AMBER_WARN}; }
        to { text-shadow: 0 0 3px ${COLORS.BG_PRIMARY}, 0 0 20px ${COLORS.AMBER_WARN}, 0 0 30px ${COLORS.MALWARE_RED}; }
      }
      @media (prefers-reduced-motion: reduce) {
        #hud-wave.hud-wave-overload .hud-wave-num { animation: none; }
        .hud-wave-num { transition: none; }
        .hud-wave-num.hud-flash { filter: none; transform: none; }
      }
      /* ── SCORE (mockup '.scorecell' '--cl:cyan') — 'SCORE n' + WORLD-LEAD flag,
         the '.race' track + '#1' marker, the YOU·vs meta. Prominent but
         CONTAINED (a bounded race, the slack collects before the controls). ──── */
      /* SCORE — the top bar's hero read; grows to fill so the bar never leaves an
         awkward gap before the right-pinned ⚙/speed controls. */
      #hud-score {
        flex: 1 1 auto;
        min-width: 200px;
      }
      .hud-score-num {
        white-space: nowrap;
        font-variant-numeric: tabular-nums;
        color: ${COLORS.TEXT_GLITCH};
        font-size: ${withUiScale(18)};
        /* O-2a — same fix as .hud-credits-val (see there for root-cause). */
        transition: filter ${UI_DURATION.flash}ms ${UI_EASE.decay},
          transform ${UI_DURATION.hover}ms ${UI_EASE.snap};
      }
      @media (prefers-reduced-motion: reduce) {
        .hud-score-num { transition: none; }
      }
      .hud-race {
        position: relative;
        height: 15px;
        border-radius: 3px;
        background: color-mix(in srgb, ${COLORS.ICE_CYAN} 7%, ${PANEL.BG});
        overflow: visible;
        box-shadow: inset 0 0 0 1px rgba(255,255,255,.05);
      }
      .hud-race-fill {
        position: absolute;
        left: 0; top: 0; bottom: 0;
        width: 0%;
        border-radius: 3px;
        background: linear-gradient(90deg,
          color-mix(in srgb, ${COLORS.ICE_CYAN} 50%, #000),
          ${COLORS.ICE_CYAN} 60%, var(--hud-gold-hi));
        box-shadow: 0 0 10px color-mix(in srgb, ${COLORS.ICE_CYAN} 50%, transparent);
        transition: width .2s;
      }
      /* the '#1' target marker tick (mockup '.rec' + '.rec::after'). */
      .hud-race-rec {
        position: absolute;
        top: -3px; bottom: -3px; right: -1px;
        width: 2px;
        background: var(--hud-gold);
        box-shadow: 0 0 7px var(--hud-gold);
        z-index: 4;
      }
      .hud-race-rec::after {
        content: "#1";
        position: absolute;
        top: -13px; left: 50%;
        transform: translateX(-50%);
        font-family: ${UI_FONT_STACK};
        font-size: ${roleFontSize("micro")};
        color: var(--hud-gold-hi);
      }
      .hud-score-meta {
        display: flex;
        justify-content: space-between;
        gap: 10px;
        font-family: ${UI_FONT_STACK};
        font-size: ${roleFontSize("caption")};
        letter-spacing: 0.8px;
        margin-top: 1px;
        white-space: nowrap;
      }
      .hud-score-you { color: ${COLORS.ICE_CYAN}; }
      .hud-score-vs {
        color: var(--hud-gold);
        overflow: hidden;
        text-overflow: ellipsis;
        min-width: 0;
      }
      /* WORLD LEAD — the gold overflow flag (data-gold / CLASS-Ω). */
      .hud-lead-flag {
        display: none;
        color: ${COLORS.BG_PRIMARY};
        background: var(--hud-gold);
        padding: 0 6px;
        border-radius: 3px;
        font-family: ${UI_FONT_STACK};
        font-size: ${roleFontSize("caption")};
        letter-spacing: 1px;
        box-shadow: 0 0 12px var(--hud-gold);
        animation: hud-lead-pulse 1.4s ease-in-out infinite;
      }
      .hud-score-lead .hud-race-fill {
        width: 100% !important;
        background: linear-gradient(90deg, var(--hud-gold-dk), var(--hud-gold) 50%, var(--hud-gold-hi));
        box-shadow: 0 0 16px var(--hud-gold), 0 0 30px color-mix(in srgb, ${RARITY.legendary.mid} 50%, transparent);
        animation: hud-lead-pulse 1.4s ease-in-out infinite;
      }
      .hud-score-lead .hud-lead-flag { display: inline-block; }
      .hud-score-lead .hud-score-num {
        color: var(--hud-gold);
        text-shadow: 0 0 10px color-mix(in srgb, ${RARITY.legendary.mid} 60%, transparent);
      }
      @keyframes hud-lead-pulse { 0%,100% { filter: brightness(1); } 50% { filter: brightness(1.25); } }
      @media (prefers-reduced-motion: reduce) {
        .hud-lead-flag, .hud-score-lead .hud-race-fill { animation: none; }
      }
      /* ── MULT (mockup '.multcell' '--cl:amber', left-aligned) — the big ×N + a
         column with the thin combo-window timer + the breakdown. ────────────── */
      #hud-mult { flex-shrink: 1; min-width: 150px; align-items: flex-start; }
      .hud-mult-row { display: flex; align-items: baseline; gap: 8px; }
      .hud-mult-val {
        font-size: ${withUiScale(28)};
        line-height: 0.8;
        color: var(--hud-dim);
        transition: color ${UI_DURATION.flash}ms ${UI_EASE.settle};
      }
      .hud-mult-val.hud-mult-hot {
        color: var(--hud-gold-hi);
        text-shadow: 0 0 12px color-mix(in srgb, ${COLORS.AMBER_WARN} 70%, transparent);
      }
      .hud-mult-right { display: flex; flex-direction: column; gap: 2px; min-width: 0; }
      .hud-mult-timer {
        position: relative;
        height: 5px;
        width: 110px;
        max-width: 100%;
        border-radius: 3px;
        background: color-mix(in srgb, ${COLORS.AMBER_WARN} 14%, ${PANEL.BG});
        overflow: hidden;
        margin-top: 3px;
      }
      .hud-mult-timer-fill {
        position: absolute;
        left: 0; top: 0; bottom: 0;
        width: 0%;
        background: linear-gradient(90deg, var(--hud-amber), color-mix(in srgb, ${COLORS.AMBER_WARN} 70%, #fff));
        box-shadow: 0 0 8px color-mix(in srgb, ${COLORS.AMBER_WARN} 70%, transparent);
        transition: width 90ms linear;
      }
      .hud-mult-timer-fill.hud-mult-timer-low {
        background: linear-gradient(90deg, ${COLORS.ORACLE_RED}, ${COLORS.AMBER_WARN});
      }
      .hud-mult-brk {
        font-size: ${roleFontSize("micro")};
        letter-spacing: 0.5px;
        color: color-mix(in srgb, ${COLORS.SENTINEL_GREEN} 28%, #9fb0a4);
        margin-top: 2px;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        max-width: 160px;
      }
      @media (prefers-reduced-motion: reduce) {
        .hud-mult-val { transition: none; }
        .hud-mult-timer-fill { transition: none; }
      }
      #hud-synergy {
        color: ${COLORS.ICE_CYAN};
        font-size: ${roleFontSize("micro")};
        opacity: 0.85;
        flex-shrink: 1;
        min-width: 0;
        max-width: 300px;
        overflow: hidden;
        text-overflow: ellipsis;
      }
      #hud-endless {
        color: ${COLORS.AMBER_WARN};
        font-size: ${roleFontSize("micro")};
        letter-spacing: 1px;
        align-self: center;
        border: 1px solid color-mix(in srgb, ${COLORS.AMBER_WARN} 45%, transparent);
        padding: 2px 8px;
        border-radius: 4px;
        background: color-mix(in srgb, ${COLORS.AMBER_WARN} 10%, transparent);
        flex-shrink: 1;
        min-width: 0;
        overflow: hidden;
        text-overflow: ellipsis;
      }
      @keyframes hud-pulse { from { opacity: 0.7; } to { opacity: 1; } }
      /* ── CONTROLS (mockup '.controls') — '1X' inline + the ⚙ gear; always
         pinned right, gear+speed always fit (flex-shrink:0). ─────────────────── */
      .hud-controls {
        margin-left: auto;
        display: flex;
        flex-direction: row;
        align-items: center;
        gap: 8px;
        padding: 0 12px;
        position: relative;
        flex-shrink: 0;
      }
      /* CR-7 — iPhone-only top-bar corner clearance. Per BL-219 the #hud-row must
         NOT be inset (it would break column alignment with the rail/board below);
         instead we pad the bar's FIRST (SCORE) and LAST (⚙ controls) cells inward
         so they clear the rounded display corners, while the bar box itself stays
         full-width. iPad/desktop (never .phone-tier) are byte-identical.
         D-335 — both nudges are now DERIVED from their OWN side's real inset
         (SCORE sits at the bar's LEADING/left edge -> --inset-left-design;
         the controls sit at the TRAILING/right edge -> --inset-right-design
         — each already correct for whichever edge the OS reports the Island
         on, so a 180° landscape flip is handled with no extra branching).
         max(today's px, …) falls back to the existing 30px/28px when the
         device reports no inset on that side. */
      html.phone-tier #hud-score { padding-left: max(30px, var(--inset-left-design, 0px)); }
      html.phone-tier .hud-controls { padding-right: max(28px, var(--inset-right-design, 0px)); }
      .hud-ff-btn { flex-shrink: 0; }
      .hud-ff-active { animation: hud-pulse 0.6s ease-in-out infinite alternate; }
      /* BL-130 — the shared gear BUTTON material (button only; see injectStyles) */
${gearMenuStyles("hud", { fullButton: false, buttonOnly: true })}
      #hud-speed {
        color: ${COLORS.AMBER_WARN};
        align-self: center;
        animation: hud-pulse 0.6s ease-in-out infinite alternate;
        flex-shrink: 0;
        white-space: nowrap;
      }
      /* BL-62 — the commentary TV is the PERMANENT fixture docked in-flow at the
        BASE of the LEFT column (inside #comms-dock), NOT a floating #ui-layer
        overlay (the signed composition; D-197 re-homes D-137). It FILLS the
        reserved dock zone; pointer-events:none keeps it a passive monitor that
        never intercepts a HUD click. */
      #comms-dock .hud-commentary-tv {
        width: 100%;
        height: 100%;
        box-sizing: border-box;
        pointer-events: none;
      }
      .hud-mascot-glitch { animation: hud-mascot-glitch 0.4s steps(2, end) 3; }
      @keyframes hud-mascot-glitch {
        0%, 100% { filter: none; transform: translate(0, 0); }
        25% {
          filter: drop-shadow(2px 0 ${COLORS.ORACLE_RED}) drop-shadow(-2px 0 ${COLORS.ICE_CYAN});
          transform: translate(1px, 0);
        }
        75% {
          filter: drop-shadow(-2px 0 ${COLORS.ORACLE_RED}) drop-shadow(2px 0 ${COLORS.ICE_CYAN});
          transform: translate(-1px, 0);
        }
      }
      @media (prefers-reduced-motion: reduce) {
        .hud-mascot-glitch { animation: none !important; }
      }
      /* ── the ONE lit-CRT control material (mockup '.btn') — "lit hardware":
        a faint surface gradient, an emissive edge, a tight filament glow; the
        warn (1×) + danger (EXIT) modifiers re-tint from the central palette. ── */
      .hud-btn {
        font-family: ${UI_FONT_STACK};
        font-size: ${roleFontSize("body")};
        letter-spacing: 1px;
        padding: 7px 11px;
        cursor: pointer;
        flex-shrink: 0;
        white-space: nowrap;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        gap: 6px;
        color: ${COLORS.SENTINEL_GREEN};
        border: 1px solid color-mix(in srgb, ${COLORS.SENTINEL_GREEN} 45%, transparent);
        border-radius: 6px;
        background: color-mix(in srgb, ${COLORS.SENTINEL_GREEN} 5%, ${PANEL.BG});
        box-shadow: 0 0 9px -4px color-mix(in srgb, ${COLORS.SENTINEL_GREEN} 70%, transparent);
        transition: transform ${UI_DURATION.hover}ms ${UI_EASE.snap},
          box-shadow ${UI_DURATION.hover}ms ${UI_EASE.settle},
          border-color ${UI_DURATION.hover}ms ${UI_EASE.settle},
          background ${UI_DURATION.hover}ms ${UI_EASE.settle},
          color ${UI_DURATION.hover}ms ${UI_EASE.settle};
      }
      .hud-btn:hover {
        transform: translateY(-1px);
        background: color-mix(in srgb, ${COLORS.SENTINEL_GREEN} 12%, ${PANEL.BG});
        box-shadow: 0 0 12px -2px color-mix(in srgb, ${COLORS.SENTINEL_GREEN} 55%, transparent);
      }
      .hud-btn:active { transform: translateY(0) scale(0.98); }
      /* Warn (1× / fast-forward) — amber edge/glow. */
      .hud-btn--warn {
        color: ${COLORS.AMBER_WARN};
        border-color: color-mix(in srgb, ${COLORS.AMBER_WARN} 55%, transparent);
        background: color-mix(in srgb, ${COLORS.AMBER_WARN} 6%, ${PANEL.BG});
        box-shadow: 0 0 9px -4px color-mix(in srgb, ${COLORS.AMBER_WARN} 70%, transparent);
      }
      .hud-btn--warn:hover {
        border-color: ${COLORS.AMBER_WARN};
        background: color-mix(in srgb, ${COLORS.AMBER_WARN} 14%, ${PANEL.BG});
        box-shadow: 0 0 12px -2px color-mix(in srgb, ${COLORS.AMBER_WARN} 50%, transparent);
      }
      .hud-btn--danger {
        color: ${COLORS.ORACLE_RED};
        border-color: color-mix(in srgb, ${COLORS.ORACLE_RED} 55%, transparent);
        box-shadow: 0 0 9px -4px color-mix(in srgb, ${COLORS.ORACLE_RED} 60%, transparent);
      }
      .hud-btn--danger:hover {
        background: ${COLORS.ORACLE_RED};
        color: ${COLORS.BG_PRIMARY};
        border-color: ${COLORS.ORACLE_RED};
        box-shadow: 0 0 12px color-mix(in srgb, ${COLORS.ORACLE_RED} 55%, transparent);
      }
      .hud-ff-btn.hud-ff-active {
        background: ${COLORS.AMBER_WARN};
        color: ${COLORS.BG_PRIMARY};
        border-color: ${COLORS.AMBER_WARN};
        box-shadow: 0 0 12px color-mix(in srgb, ${COLORS.AMBER_WARN} 55%, transparent);
      }
      @media (prefers-reduced-motion: reduce) {
        .hud-btn { transition: none; }
        .hud-btn:hover { transform: none; }
        .hud-btn:active { transform: none; }
      }
      /* ── RESPONSIVE — priority collapse on the TOP bar's OWN width (S146/BL-140:
        it now carries only Score + Multiplier + the ⚙/speed controls). Shed the
        secondary meta first, then the combo timer; the SCORE + ×N numbers never
        vanish and gear+speed always fit. The left HUD (Credits/Integrity/Wave)
        lives in the fixed-width left column and needs no width collapse. ─────── */
      #hud-bar.hud-narrow .hud-score-meta { display: none; }
      #hud-bar.hud-narrow .hud-mult-brk { display: none; }
      #hud-bar.hud-narrow #hud-mult { min-width: 84px; }
      #hud-bar.hud-narrow .hud-cell { padding: 0 10px; }
      #hud-bar.hud-tight .hud-mult-timer { display: none; }
      #hud-bar.hud-tight #hud-mult { min-width: 64px; }
      #hud-bar.hud-micro .hud-race { display: none; }
      #hud-bar.hud-micro #hud-score { min-width: 0; }
      #hud-bar.hud-micro .hud-btn--warn { padding: 7px 8px; }
    `;
    document.head.appendChild(style);
  }
}

function now(): number {
  return typeof performance !== "undefined" ? performance.now() : Date.now();
}

/** Thin-space thousands grouping for the score-race target (render-only). */
function groupThousands(n: number): string {
  return Math.round(n)
    .toString()
    .replace(/\B(?=(\d{3})+(?!\d))/g, " ");
}
